import {
    AmountSeries,
    SupplierSeries
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/utils/types';
import Highcharts from '../high-charts-loader';
import { ChartItem, ColorRegistry } from './chart-types';

const abbreviatorFormatter = (value: number) => {
    const prefix = '$';
    if (value === 0) {
        return '';
    } else if (value >= 1000000000) {
        const formatted = Highcharts.numberFormat(value / 1000000000, 1);
        return prefix + (formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted) + 'B';
    } else if (value >= 1000000) {
        const formatted = Highcharts.numberFormat(value / 1000000, 1);
        return prefix + (formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted) + 'M';
    } else if (value >= 1000) {
        const formatted = Highcharts.numberFormat(value / 1000, 1);
        return prefix + (formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted) + 'K';
    } else {
        return prefix + value.toString();
    }
};

const getDataLabelAbbreviator = () => {
    return {
        dataLabels: {
            enabled: true,
            formatter: function (): string {
                const _this: any = this;
                return abbreviatorFormatter(_this.y);
            },
            style: getTextStyle()
        }
    };
};

const parseToShortDate = (dateList: Date[]): string[] => {
    return dateList.map((date: Date) => {
        const month = date.getMonth() + 1;
        return `${month >= 10 ? month : '0' + month}-${date.getFullYear().toString().slice(-2)}`;
    });
};

const parseToMonthYear = (dateList: Date[]): string[] => {
    return dateList.map((date: Date) => {
        const month = date.getMonth();
        const monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
        ];
        return `${monthNames[month]} ${date.getFullYear()}`;
    });
};

const getTextStyle = (size = 13) => ({
    fontSize: `${size}px`,
    fontFamily: 'Muli, Helvetica, Arial, sans-serif'
});

const elevenColors = [
    '#122452',
    '#003F6C',
    '#005877',
    '#007071',
    '#008661',
    '#33A65C',
    '#6AC54A',
    '#B9CD3A',
    '#FFD146',
    '#FFB341',
    '#647682'
];

export const earlyPaidColors = ['#122452', '#FFD146'];

const filterColors = (
    suppliersRegistry: ColorRegistry,
    filterSuppliers: string[],
    earlyPaid: boolean = false
): string[] => {
    if (earlyPaid) {
        return earlyPaidColors;
    }
    let colorProjection: string[] = [];
    for (let supplier of Object.keys(suppliersRegistry)) {
        if (filterSuppliers.includes(supplier) || filterSuppliers.length === 0) {
            colorProjection.push(suppliersRegistry[supplier]);
        }
    }
    colorProjection.push(elevenColors[10]);
    return colorProjection;
};

const countMatchingColors = (
    suppliersRegistry: ColorRegistry,
    filterSuppliers: string[]
): number => {
    let count = 0;
    for (let supplier of Object.keys(suppliersRegistry)) {
        if (filterSuppliers.includes(supplier) || filterSuppliers.length === 0) {
            count += 1;
        }
    }
    if (Object.keys(suppliersRegistry).length === 0) {
        count = 10;
    }
    return count;
};

const sumElementsInArray = (r: number[], a: number[]) => r.map((b: number, i: number) => a[i] + b);

export const RemainingSuppliersLabel = `Remaining Suppliers`;

export default {
    abbreviatorFormatter,
    getDataLabelAbbreviator,
    parseToShortDate,
    parseToMonthYear,
    getTextStyle,
    elevenColors,
    filterColors,
    sumElementsInArray,
    countMatchingColors
};

export const spendVolumeSort = (a: SupplierSeries, b: SupplierSeries) => {
    const totalA = a.amountSeries.reduce(
        (total: number, serie: AmountSeries) => total + serie.invoiceAmount.cents,
        0
    );
    const totalB = b.amountSeries.reduce(
        (total: number, serie: AmountSeries) => total + serie.invoiceAmount.cents,
        0
    );
    return totalB - totalA;
};

export const earlyPaidSort = (a: SupplierSeries, b: SupplierSeries) => {
    const totalA = a.amountSeries.reduce(
        (total: number, serie: AmountSeries) => total + serie.fundedAmount.cents,
        0
    );
    const totalB = b.amountSeries.reduce(
        (total: number, serie: AmountSeries) => total + serie.fundedAmount.cents,
        0
    );
    return totalB - totalA;
};

export const dateSort = (a: any, b: any) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
};

export const mapCentsToDollars = (amount: number | ChartItem) => {
    if (amount === 0) return amount;
    return (amount as number) / 100;
};

export const categorySort = (a: SupplierSeries, b: SupplierSeries) => {
    const totalA = a.amountSeries.length;
    const totalB = b.amountSeries.length;
    return totalB - totalA;
};

export const sumInvoiceAmount = (
    categories: AmountSeries[],
    sourceData: SupplierSeries[]
): number[] => {
    let plainCategories = Array(categories.length).fill(0);
    for (const supp of sourceData) {
        for (const [index, timeSlot] of categories.entries()) {
            const slot = supp.amountSeries.find(
                (value: AmountSeries) => value.date === timeSlot.date
            );
            if (!!slot) {
                plainCategories[index] = (plainCategories[index] ?? 0) + slot.invoiceAmount.cents;
            }
        }
    }
    return plainCategories;
};

export const segregateSuppliers = (slideNumber: number, chartData: ChartItem[]): ChartItem[] => {
    if (slideNumber > 0) {
        const addRemaining = chartData.length > slideNumber;
        const top = chartData.slice(0, slideNumber);
        if (addRemaining) {
            chartData = top.concat({
                name: RemainingSuppliersLabel,
                data: chartData
                    .slice(slideNumber)
                    .map((chartItem: ChartItem) => chartItem.data)
                    .reduce(sumElementsInArray)
            });
        }
    } else {
        chartData = [
            {
                name: RemainingSuppliersLabel,
                data: chartData
                    .map((chartItem: ChartItem) => chartItem.data)
                    .reduce(sumElementsInArray)
            }
        ];
    }
    return chartData;
};
